import React, { useCallback, useEffect, useRef, useState } from "react";
import "./content.css";
import { Cascader, Input, Button } from "antd";
import { toggleClassByCategory } from "./editorHelpers/toggleClassByCategory";
import { wrapSelectedText } from "./editorHelpers/wrapSelectedText";
import { insertHtmlSnippet } from "./editorHelpers/insertHtmlSnippet";
import { cleanSelectedElement } from "./editorHelpers/cleanSelectedElement";
import { deleteSelectedElement } from "./editorHelpers/deleteSelectedElement";
import { handleEditorClick } from "./editorHelpers/handleEditorClick";
import TextArea from "antd/es/input/TextArea";

interface ContentEditorProps {
    value?: string;
    onChange?: (value: string) => void;
}

export const ContentEditor = ({ value = "", onChange }: ContentEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [classInput, setClassInput] = useState("");
    const [attrState, setAttrState] = useState<Record<string, string>>({});
    const [customHtml, setCustomHtml] = useState("");
    const [showCustomHtmlInput, setShowCustomHtmlInput] = useState(false);


    const colors = ["red", "blue", "green", "indigo", "orange"];
    const listTypes = ["list-disc", "list-circle", "list-decimal", "list-smallletter", "list-largeletter"];

    useEffect(() => {
        if (selectedEl) setClassInput(selectedEl.className);
    }, [selectedEl]);

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value;
        }
    }, [value]);

    useEffect(() => {
        if (!editorRef.current) return;

        const observer = new MutationObserver(() => {
            onChange?.(editorRef.current!.innerHTML);
        });

        observer.observe(editorRef.current, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
        });

        return () => observer.disconnect();
    }, [onChange]);

    const [jsonValue, setJsonValue] = useState("{}");

    // init
    useEffect(() => {
        if (!selectedEl) return;
        const val = selectedEl.getAttribute("data-values");
        try {
            const parsed = val ? JSON.parse(val) : {};
            setJsonValue(JSON.stringify(parsed, null, 2)); // pretty print
        } catch {
            setJsonValue(val || "{}");
        }
    }, [selectedEl]);

    useEffect(() => {
        if (!selectedEl) return;
        const attrs: Record<string, string> = {};
        Array.from(selectedEl.attributes).forEach(attr => {
            if (attr.name !== 'style') { // style’ı hariç tut
                attrs[attr.name] = attr.value;
            }
        });
        setAttrState(attrs);
    }, [selectedEl]);


    const handleJsonChange = (val: string) => {
        if (!selectedEl) return;
        setJsonValue(val);
        try {
            const parsed = JSON.parse(val);
            selectedEl.setAttribute("data-values", JSON.stringify(parsed));
        } catch {
            // invalid JSON, şimdilik ignore
        }
    };


    const handleInput = () => {
        if (editorRef.current) onChange?.(editorRef.current.innerHTML);
    };

    const onEditorClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            handleEditorClick(e, editorRef, selectedEl, setSelectedEl, setClassInput);
        },
        [selectedEl] // selectedEl değiştiğinde güncel değeri alacak
    );


    // etiket bazlı input alanları
    const renderInputsForTag = () => {
        if (!selectedEl) return null;
        const tag = selectedEl.tagName.toLowerCase();

        switch (tag) {
            case "a":
                return (
                    <>
                        {Object.entries(attrState).map(([name, value]) => (
                            <Input
                                key={name}
                                placeholder={name}
                                value={value}
                                onChange={(e) => {
                                    const newVal = e.target.value;
                                    setAttrState(prev => ({ ...prev, [name]: newVal }));
                                    if (selectedEl) selectedEl.setAttribute(name, newVal);
                                }}
                            />
                        ))}
                    </>
                );
            case "img":
                return (
                    <>
                        {Object.entries(attrState).map(([name, value]) => (
                            <Input
                                key={name}
                                placeholder={name}
                                value={value}
                                onChange={(e) => {
                                    const newVal = e.target.value;
                                    setAttrState(prev => ({ ...prev, [name]: newVal }));
                                    if (selectedEl) selectedEl.setAttribute(name, newVal);
                                }}
                            />
                        ))}
                    </>
                );
            case "placeholder":
                return (
                    <>
                        {Object.entries(attrState).map(([name, value]) => (
                            name == "data-values"

                                ? <TextArea
                                    wrap="off"
                                    style={{ minHeight: "300px", overflowX: 'auto', fontFamily: "monospace", fontSize: '12px' }}
                                    placeholder="data-values"
                                    value={jsonValue}
                                    onChange={(e) => handleJsonChange(e.target.value)}
                                />
                                : < Input
                                    key={name}
                                    placeholder={name}
                                    value={value}
                                    onChange={(e) => {
                                        const newVal = e.target.value;
                                        setAttrState(prev => ({ ...prev, [name]: newVal }));
                                        if (selectedEl) selectedEl.setAttribute(name, newVal);
                                    }}
                                />
                        ))}
                    </>
                );
            default:
                return <p style={{ color: "#777" }}>Bu etiket için özel input yok.</p>;
        }
    };

    return (
        <div style={{ position: "relative" }}>
            <div
                ref={editorRef}
                className="article-content"
                contentEditable
                suppressContentEditableWarning
                style={{
                    border: "1px solid #ccc",
                    padding: 10,
                    minHeight: 150,
                    borderRadius: 6,
                }}
                onInput={handleInput}
                onClick={onEditorClick}
            >
            </div>

            <div style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                position: 'fixed',
                bottom: '60px',
                left: '10px',
                zIndex: '20',
                backgroundColor: 'white',
                border: '2px solid grey',
                borderRadius: '10px',
                padding: '10px',
                minWidth: '200px',
                maxWidth: '400px',
            }}>
                {/* Tag tipi */}
                <p
                    style={{ display: 'flex', backgroundColor: '#3f51b5', borderRadius: '5px', color: 'white', padding: '2px 10px', fontSize: '15px' }}
                >{selectedEl?.tagName}</p>

                {/* sarmalayıcı */}
                <div style={{ display: "flex", gap: 8 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            border: "1px solid grey",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            const wrapSnippet = "<strong>{{selected}}</strong>";
                            wrapSelectedText(selectedEl, wrapSnippet);
                        }}
                    >
                        <strong>B</strong>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            border: "1px solid grey",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            const wrapSnippet = "<em>{{selected}}</em>";
                            wrapSelectedText(selectedEl, wrapSnippet);
                        }}
                    >
                        <em>I</em>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            border: "1px solid grey",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            const wrapSnippet = `<a href="www.google.com" target="_blank" data-link-description="" rel="noopener noreferrer">
                                                        {{selected}}
                                                 </a>
                                    `;
                            wrapSelectedText(selectedEl, wrapSnippet);
                        }}
                    >
                        <i className="fi fi-rr-link-alt"></i>
                    </div>
                </div>

                {/* renk */}
                {
                    selectedEl && (
                        <div style={{ display: "flex", gap: 8 }}>
                            {colors.map((color) => (
                                <div
                                    key={color}
                                    style={{
                                        width: 30,
                                        height: 30,
                                        backgroundColor: color,
                                        borderRadius: "5px",
                                        cursor: "pointer",
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => toggleClassByCategory(selectedEl!, color, colors)}
                                />
                            ))}
                            <div
                                style={{
                                    width: 30,
                                    height: 30,
                                    border: "1px solid grey",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => toggleClassByCategory(selectedEl!, "border", [])}

                            />
                        </div>
                    )
                }

                {/* list */}
                {
                    selectedEl && selectedEl.tagName === "UL" && (
                        <div style={{ display: "flex", gap: 8 }}>
                            {
                                listTypes.map((listType) => (
                                    <div
                                        key={listType}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 30,
                                            height: 30,
                                            border: "1px solid grey",
                                            borderRadius: 5,
                                            cursor: "pointer",
                                            fontSize: 12,
                                            fontWeight: "bold",
                                        }}
                                        onMouseDown={(e) => e.preventDefault()} // tıklamada selection kaybolmasın
                                        onClick={() => toggleClassByCategory(selectedEl!, listType, listTypes)}
                                        title={listType} // hover'da tip göster
                                    >
                                        {listType === "list-disc" ? "•" : listType === "list-circle" ? "◦" : listType === "list-decimal" ? "1." : listType === "list-smallletter" ? "a." : listType === "list-largeletter" ? "A." : listType}
                                    </div>
                                ))}
                        </div>
                    )
                }

                {/* class input */}
                {
                    selectedEl && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

                            <Input
                                placeholder="class list"
                                value={classInput}
                                onChange={(e) => {
                                    setClassInput(e.target.value);
                                    if (selectedEl) selectedEl.className = e.target.value;
                                }}
                            />
                        </div>
                    )
                }

                {/* attributes */}
                {
                    selectedEl && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}> {renderInputsForTag()} </div>)
                }
                {/* custom html textarea */}
                {showCustomHtmlInput && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <TextArea
                            placeholder="HTML veya metni buraya yapıştırın"
                            value={customHtml}
                            onChange={(e) => setCustomHtml(e.target.value)}
                            style={{ minHeight: 150, fontFamily: 'monospace' }}
                        />
                        <Button
                            onClick={() => {
                                if (editorRef.current) {
                                    insertHtmlSnippet(editorRef.current, selectedEl, "after", customHtml);
                                    setCustomHtml("");
                                    setShowCustomHtmlInput(false);
                                }
                            }}
                        >
                            Ekle
                        </Button>
                    </div>
                )}


                {/* adder */}
                <div style={{ display: "flex", gap: 8 }}>
                    {selectedEl && (
                        <Cascader
                            options={insertOptions}
                            expandTrigger="hover"
                            onChange={(value) => {
                                if (!selectedEl) return;
                                const pos = value[0] as 'before' | 'after'; // root seviyedeki konum
                                const type = value[value.length - 1];       // path’in en içteki seçimi
                                if (type === "customHtml") {
                                    // input açılıyor
                                    setShowCustomHtmlInput(true);
                                    return;
                                }
                                const snippet = snippetFactory[type] || '<div>HATA GERÇEKLEŞTİ</div>';
                                insertHtmlSnippet(editorRef.current, selectedEl, pos, snippet);
                            }}
                        />
                    )}
                </div>

                {/* delete */}
                <div style={{ display: "flex", gap: 8 }}>
                    <Button
                        type="primary"
                        danger
                        style={{ width: '100%', flex: '1', }}
                        onClick={() => setSelectedEl(deleteSelectedElement(selectedEl))}
                        disabled={!selectedEl}
                    ><i className="fi fi-rr-trash"></i>
                    </Button>
                    <Button
                        danger
                        type="dashed"
                        style={{ width: '100%', flex: '1', }}
                        onClick={() => {
                            cleanSelectedElement(selectedEl);
                            setSelectedEl(null); // artık seçili yok
                        }}

                        disabled={!selectedEl}
                    >
                        <i className="fi fi-rs-eraser"></i>
                    </Button>
                    <Button
                        style={{ width: '100%', flex: '1', }}
                        onClick={() => {
                            const editor = editorRef.current;
                            if (!editor) return;
                            editor.querySelectorAll("*").forEach(el => el.removeAttribute("style"));
                            setSelectedEl(null);
                        }}
                    >
                        <i className="fi fi-rr-cursor"></i>
                    </Button>
                </div>
            </div >
        </div >
    );
};

// örnek snippet map
const snippetFactory: Record<string, string> = {
    h2: `<h2>Header h2</h2>`,
    p: `<p>Bu bir p metindir...</p>`,
    list: `<ul class="list-disc">
            <li>Örnek liste itemi 1</li>
            <li>Örnek liste itemi 2</li>
        </ul>`,
    callout: `<div class="callout red">
                        <strong>Callout başlığı</strong>
                    <p>Bu da metin</p>
              </div>`,

    placeholder: `<placeholder type="x" data-values='{"label":"Tıkla"}'>**GENEL-PLACEHOLDER**</placeholder>`,
    imgFigure: `
            <figure>
                <img
                    src="https://i.ytimg.com/vi/0RUBfAKLlf8/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLCcIIUf1u0wCW_Uw-7AAdqrPvsN5g"
                    alt="Dağ manzarası"
                />
                <figcaption>Dağ manzarası</figcaption>
            </figure>
         `,
    imgContainer: `<div class="img-container">
                        <figure>
                            <img
                                src="https://i.ytimg.com/vi/0RUBfAKLlf8/hq720.jpg?sqp=-oaymwEhCK4FEIIDSFryq4qpAxMIARUAAAAAGAElAADIQj0AgKJD&rs=AOn4CLCcIIUf1u0wCW_Uw-7AAdqrPvsN5g"
                                alt="Dağ manzarası"
                            />
                            <figcaption>Dağ manzarası</figcaption>
                        </figure>
                   </div>
    `,
};

interface Option {
    value: string;
    label: string;
    children?: Option[];
}

const insertOptions: Option[] = [
    {
        value: 'before',
        label: 'Öncesine Ekle',
        children: [
            {
                value: 'h2',
                label: 'h2 başlık',
            },
            {
                value: 'p',
                label: 'p metin',
            },
            {
                value: 'img',
                label: 'img',
                children: [
                    {
                        value: 'imgFigure',
                        label: 'img(figure)',
                    },
                    {
                        value: 'imgContainer',
                        label: 'img(container)',
                    }
                ]
            },
            {
                value: 'list',
                label: 'list',
            },
            {
                value: 'callout',
                label: 'Callout',
            },
            {
                value: 'placeholder',
                label: 'placeholder',
            },
        ],
    },
    {
        value: 'after',
        label: 'Sonrasına Ekle',
        children: [
            {
                value: 'h2',
                label: 'h2 başlık',
            },
            {
                value: 'p',
                label: 'p metin',
            },
            {
                value: 'img',
                label: 'img',
                children: [
                    {
                        value: 'imgFigure',
                        label: 'img(figure)',
                    },
                    {
                        value: 'imgContainer',
                        label: 'img(container)',
                    }
                ]
            },
            {
                value: 'list',
                label: 'list',
            },
            {
                value: 'callout',
                label: 'Callout',
            },
            {
                value: 'placeholder',
                label: 'placeholder',
            },
            {
                value: 'customHtml',
                label: 'customHtml',
            },
        ],
    },
];

